import {useHistoryAssetsByIdQuery, useHistoryOperationModesByIdQuery} from '@domain/assets'
import {getPeakLoadWindows, usePeakLoadWindowsQuery} from '@domain/electricity'
import {useHistoryStoragesWithMaterialQuery} from '@domain/material-storage'
import {useMaterialsRecipesById} from '@domain/materials'
import {
  AssetOperationTimeSource,
  COST_AVOIDANCE_FOR_RANGE_QUERY_KEY,
  ELECTRICITY_QUERY_KEY,
  getScheduleInTimeFrame,
  SCHEDULE_QUERY_KEY,
  StockDevelopmentQueryKey,
  useAddScheduleItemMutation,
  useElectricityQuery
} from '@domain/schedule'
import type {Schedule, ScheduleItem} from '@domain/schedule'
import {OperationModeType} from '@hconnect/common/types'
import {useRequiredUser} from '@hconnect/login'
import {useUpmPlantContext, useUpmPlantTimezone} from '@hconnect/upm'
import {spawnErrorToast} from '@hm/fundamental'
import {useQueryClient} from '@tanstack/react-query'
import moment, {Moment} from 'moment'
import {useTranslation} from 'react-i18next'
import {v4 as uuidv4} from 'uuid'

import {FEATURE_NAMESPACE} from '../../../consts'
import {useTrackPlannerEvent} from '../../../helpers'
import {
  doesOverlappingItemExist,
  getDefaultSelectedSilos,
  getScheduleItemPowerConsumptionQuaterlyDict
} from '../../../selectors'

type ScheduleContext = {previousState: Schedule}

interface AddScheduleItemParams {
  startOfPlan: Moment
  endOfPlan: Moment
}

export const useAddScheduleItem = ({startOfPlan, endOfPlan}: AddScheduleItemParams) => {
  const queryClient = useQueryClient()
  const trackPlannerEvent = useTrackPlannerEvent()
  const {plantId} = useUpmPlantContext()
  const timezoneId = useUpmPlantTimezone()
  const {data: operationModesById} = useHistoryOperationModesByIdQuery({
    plantId,
    timeFrame: [startOfPlan, endOfPlan]
  })
  const {data: assetsById} = useHistoryAssetsByIdQuery({
    plantId,
    timeFrame: [startOfPlan, endOfPlan]
  })
  const loggedUser = useRequiredUser()
  const {data: recipesById} = useMaterialsRecipesById({plantId, throwOnError: false})
  const {data: storagesWithMaterial} = useHistoryStoragesWithMaterialQuery({
    plantId,
    timeFrame: [startOfPlan, endOfPlan],
    throwOnError: false
  })
  const {data: peakLoadWindows} = usePeakLoadWindowsQuery({
    plantId,
    timeFrame: [startOfPlan, endOfPlan],
    throwOnError: false
  })
  const {data: electricity} = useElectricityQuery({
    plantId,
    timezoneId,
    timeFrame: [startOfPlan, endOfPlan],
    throwOnError: false
  })
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const queryVariables = {
    plantCode: plantId,
    start: startOfPlan.toISOString(),
    end: endOfPlan.toISOString()
  }

  const {mutate} = useAddScheduleItemMutation({
    mutationKey: ['addScheduleItem'],
    onMutate: async (variables) => {
      await queryClient.cancelQueries({queryKey: [SCHEDULE_QUERY_KEY]})

      const previousState = queryClient.getQueryData<Schedule>([SCHEDULE_QUERY_KEY, queryVariables])

      if (previousState) {
        const id = uuidv4()
        const updatedSchedule: Schedule = {
          ...previousState,
          schedules: {
            ...previousState.schedules,
            [id]: {
              ...variables.scheduleItemDTO,
              // temp id for the optimistic update
              id,
              isShutdownAvailable: true,
              source: AssetOperationTimeSource.Manual,
              selectedSilos: {
                isOrderSpecified: false,
                ids: []
              },
              updatedOn: new Date().toISOString(),
              updatedBy: loggedUser.name ?? 'Optimistic update'
            }
          }
        }
        queryClient.setQueryData([SCHEDULE_QUERY_KEY, queryVariables], updatedSchedule)
      }
      return {previousState} as ScheduleContext
    },
    onError: (error) => {
      const detail = (error.response?.data as {detail?: string} | undefined)?.detail
      if (detail) {
        spawnErrorToast(detail)
      }
    },
    onSuccess: (_, {scheduleItemDTO}) => {
      trackPlannerEvent({name: 'onAddScheduleItem'})
      void queryClient.invalidateQueries({
        queryKey: [COST_AVOIDANCE_FOR_RANGE_QUERY_KEY],
        refetchType: 'all'
      })
      const isProductionModeItem =
        operationModesById![scheduleItemDTO.assetOperationModeId].type ===
        OperationModeType.Production
      // we need to invalidate stockDevelopment query only if we add production mode item
      if (isProductionModeItem) {
        void queryClient.invalidateQueries({
          queryKey: [StockDevelopmentQueryKey],
          refetchType: 'all'
        })
      }
    },
    onSettled: () => {
      // invalidating all schedules for all start/end params
      void queryClient.invalidateQueries({queryKey: [SCHEDULE_QUERY_KEY], refetchType: 'all'})
      void queryClient.invalidateQueries({
        queryKey: [ELECTRICITY_QUERY_KEY, {plantCode: plantId}],
        refetchType: 'all'
      })
    }
  })

  return (
    newScheduleItem: Pick<
      ScheduleItem,
      'start' | 'end' | 'assetOperationModeId' | 'isTransitionTime' | 'assetId'
    >
  ) => {
    const schedule = queryClient.getQueryData<Schedule>([SCHEDULE_QUERY_KEY, queryVariables])
    if (
      !schedule ||
      !electricity ||
      !peakLoadWindows ||
      !storagesWithMaterial ||
      !recipesById ||
      !operationModesById ||
      !assetsById
    )
      return
    const activeSchedules = getScheduleInTimeFrame(schedule.schedules, moment.utc(), endOfPlan)

    const operationMode = operationModesById[newScheduleItem.assetOperationModeId]

    const scheduleItemDTO = {
      ...newScheduleItem,
      selectedSilos: getDefaultSelectedSilos(operationMode, recipesById, storagesWithMaterial),
      isFixed: false
    }

    const hasOverlap = doesOverlappingItemExist(activeSchedules, scheduleItemDTO)

    const powerConsumptionForUpdatedItemDict = getScheduleItemPowerConsumptionQuaterlyDict(
      scheduleItemDTO,
      assetsById[newScheduleItem.assetId].operationModes.find(
        (operationMode) => operationMode.id === newScheduleItem.assetOperationModeId
      )!
    )

    const prevTotalPowerConsumption = electricity.planned
    const adjustedPowerConsumption = prevTotalPowerConsumption.map((energyConsumption) => {
      const newItemPower = powerConsumptionForUpdatedItemDict[energyConsumption.dateTimeIso] || 0
      return {...energyConsumption, power: energyConsumption.power + newItemPower}
    })
    const updatedPeakLoadWindows = getPeakLoadWindows({
      powerConsumption: adjustedPowerConsumption,
      peakLoadWindows: peakLoadWindows
    })
    const hasPeakLoadWindowsViolated = updatedPeakLoadWindows.some((plw) => plw.violated)

    if (hasPeakLoadWindowsViolated) {
      spawnErrorToast(t('error.planViolatesPeakLoadWindow'))
      return
    }
    if (hasOverlap) {
      spawnErrorToast(t('error.overlappingItemExists'))
      return
    }
    mutate({
      plantCode: plantId,
      scheduleId: schedule.scheduleId,
      scheduleItemDTO
    })
  }
}
